import React from 'react'
import PropTypes, { ValidationMap } from 'prop-types'
import {
  Modal,
  ModalClose,
  ModalContent,
  Tile,
  TileImage,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { TrackContentType } from '@mc/track-event'
import { VideoAutoplay, VideoEventHandlers, VideoOverlay } from '@mc/video'
import Image from 'components/LegacyImage'
import SubscribeVideoScreen from 'components/VideoModal/SubscribeVideoScreen'
import { VideoPlayer } from 'components/VideoPlayer'
import { childrenType } from 'libs/types'
import ContactSalesVideoScreen from './ContactSalesVideoScreen'

interface Props {
  show?: boolean
  isSample?: boolean
  isEnterprise?: boolean
  closeModalOnPauseScreenCTAClick?: boolean
  canClickOutsideToClose?: boolean
  onCloseModal?: () => void
  videoId?: string | null
  mediaUuid?: string
  courseId?: string | number
  author?: string | null
  contentId: string | number
  contentTitle: string
  contentType: TrackContentType
  tagline?: string | null
  instructorAvatar?: string | null
  buttonGroup?: React.ReactNode
  onPlay?: () => void
  onPause?: () => void
  onEnd?: () => void
  onSeek?: () => void
  onSeeked?: () => void
  onTimeChange?: () => void
  signUpHref?: string
  trackProps?: {
    uuid?: string
    categorySlug?: string
  }
}

const VideoModal = ({
  show,
  isSample = false,
  isEnterprise = false,
  closeModalOnPauseScreenCTAClick = false,
  canClickOutsideToClose = true,
  onCloseModal,
  videoId,
  mediaUuid,
  courseId,
  author,
  contentId,
  contentTitle,
  contentType,
  tagline,
  instructorAvatar,
  buttonGroup,
  onPlay,
  onPause,
  onEnd,
  onSeek,
  onSeeked,
  onTimeChange,
  signUpHref = '',
  trackProps = {},
}: Props) => {
  const { t } = useTranslation('@mc/next')
  const modalHeadingKey = isSample
    ? 'course.sample.modal.heading'
    : 'course.trailer.modal.heading'

  if (!videoId && !mediaUuid) return <></>

  const renderPauseScreen = () =>
    isEnterprise ? (
      <ContactSalesVideoScreen
        isSample={isSample}
        onCTAClick={closeModalOnPauseScreenCTAClick && onCloseModal}
      />
    ) : (
      <SubscribeVideoScreen
        isSample={isSample}
        signUpHref={signUpHref}
        trackProps={trackProps}
        chapterImage={undefined}
      />
    )

  return (
    <Modal
      backdrop='extra-dark'
      show={show}
      onClose={onCloseModal}
      canClickOutsideToClose={canClickOutsideToClose}
    >
      <ModalClose />
      <ModalContent aria-labelledby='trailer-modal-heading'>
        <div
          className='row justify-content-between align-items-center mc-mb-2 mc-overflow--hidden'
          data-testid='trailer-modal'
        >
          {instructorAvatar && (
            <div className='d-none d-md-block col-2 col-md-1'>
              <Tile aspectRatio='1x1'>
                <TileImage
                  className='mc-corners--circle'
                  image={<Image alt='' src={instructorAvatar} layout='fill' />}
                />
              </Tile>
            </div>
          )}

          <div className='col'>
            <h1
              className='mc-text-h5 mc-text--1-line-max'
              id='trailer-modal-heading'
            >
              {t(modalHeadingKey, { title: author })}
            </h1>
            <p className='mc-mt-1'>{tagline}</p>
          </div>

          <div className='col-auto'>{buttonGroup}</div>
        </div>
        <VideoPlayer
          contentId={contentId}
          contentTitle={contentTitle}
          contentType={contentType}
          courseId={courseId}
          videoId={videoId ?? undefined}
          mediaUuid={mediaUuid}
        >
          <VideoEventHandlers
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnd}
            onSeeking={onSeek}
            onSeeked={onSeeked}
            onTimeUpdate={onTimeChange}
          />
          <VideoAutoplay />
          <VideoOverlay state={['paused', 'ended']} fade>
            {renderPauseScreen()}
          </VideoOverlay>
        </VideoPlayer>
      </ModalContent>
    </Modal>
  )
}

VideoModal.propTypes = {
  videoId: PropTypes.string,
  mediaUuid: PropTypes.string,
  show: PropTypes.bool,
  isSample: PropTypes.bool,
  isEnterprise: PropTypes.bool,
  closeModalOnPauseScreenCTAClick: PropTypes.bool,
  canClickOutsideToClose: PropTypes.bool,
  onCloseModal: PropTypes.func,
  author: PropTypes.string,
  contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  contentTitle: PropTypes.string.isRequired,
  contentType: PropTypes.string.isRequired,
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tagline: PropTypes.string,
  instructorAvatar: PropTypes.string,
  buttonGroup: childrenType,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnd: PropTypes.func,
  onSeek: PropTypes.func,
  onSeeked: PropTypes.func,
  onTimeChange: PropTypes.func,
  signUpHref: PropTypes.string,
  trackProps: PropTypes.shape({
    uuid: PropTypes.string,
    categorySlug: PropTypes.string,
  }),
} as ValidationMap<Props>

export default VideoModal
